var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api, apiResolve, AppSnackbar, CoreEvent } from '@sierralabs/core-ui';
import { useAssignEntityAsync } from '@sierralabs/entity-ui';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteEvent } from '../../hooks/event/useSiteEvent';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
import { AssignEntityEvent, AssignEntityWarning, useAssignEntityComponent } from './AssignEntityPlugin';
import { SiteButtonBase, SiteButtonBaseConfigurator } from './SiteButtonPlugin';
import { SiteCreateEntityDialogEvent } from './SiteCreateEntityDialogPlugin';
import { createSiteTableFilterExpressionMap } from './SiteTablePlugin';
export const AssignEntityCreateButton = props => {
    var _a, _b, _c, _d, _e, _f, _g;
    const params = useParams();
    const component = useSubscribeComponentChanges(AssignEntityCreateButtonPlugin, props);
    const { editing, isHover, hoverProps } = useSiteContext();
    const { emit } = useSiteEvent(component);
    const assignEntityComponent = useAssignEntityComponent(component);
    const assignEntityComponentId = assignEntityComponent === null || assignEntityComponent === void 0 ? void 0 : assignEntityComponent.id;
    // get the assign dialog from the assign entity component
    // and use props.assign for the assignEntity hook
    const assignDialog = (_a = assignEntityComponent === null || assignEntityComponent === void 0 ? void 0 : assignEntityComponent.children) === null || _a === void 0 ? void 0 : _a.find((c) => c.slot === 'dialog');
    // get table component from assignEntity -> card -> table
    const cardTable = (_d = (_c = (_b = assignEntityComponent === null || assignEntityComponent === void 0 ? void 0 : assignEntityComponent.children) === null || _b === void 0 ? void 0 : _b.find(c => c.slot === 'card')) === null || _c === void 0 ? void 0 : _c.children) === null || _d === void 0 ? void 0 : _d.find((c) => c.slot === 'table');
    const { assign, multiple } = (assignDialog === null || assignDialog === void 0 ? void 0 : assignDialog.props) || {};
    const { isLoading, error, assignEntity, setState } = useAssignEntityAsync(assign || {});
    const queryId = (_e = cardTable === null || cardTable === void 0 ? void 0 : cardTable.queryIds) === null || _e === void 0 ? void 0 : _e[0];
    // run query for card table here to save it in context
    const filter = (_f = cardTable === null || cardTable === void 0 ? void 0 : cardTable.props) === null || _f === void 0 ? void 0 : _f.filter;
    const filterTarget = (filter === null || filter === void 0 ? void 0 : filter.routeParam)
        ? params[filter === null || filter === void 0 ? void 0 : filter.routeParam]
        : undefined;
    const classes = ['assign-entity-create-button'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    // handle create entity event
    useEffect(() => {
        return CoreEvent.handle(SiteCreateEntityDialogEvent.AfterCreate, (data) => __awaiter(void 0, void 0, void 0, function* () {
            // make sure create entity event is triggered
            // under the same assign-entity component
            // and that the necessary properties are provided
            if (!data ||
                !data.data ||
                !data.eventData ||
                !assignEntityComponentId ||
                assignEntityComponentId !== data.eventData.assignEntityComponentId) {
                return;
            }
            const createdEntity = data.data;
            // for multiple assignment, get existing relations to append created entity
            let entities = null;
            if (multiple) {
                const expressionMap = filter &&
                    createSiteTableFilterExpressionMap(filter.field || '', filterTarget);
                const { data: cardQueryData } = yield apiResolve(api.post(`queries/${queryId}/run`, { expressionMap }));
                const selectedItems = Array.isArray(cardQueryData)
                    ? cardQueryData
                    : cardQueryData
                        ? [cardQueryData]
                        : [];
                entities = selectedItems.concat(createdEntity);
            }
            else {
                entities = createdEntity;
            }
            // assign created entity
            const { result } = assignEntity(entities);
            const { data: updatedEntity } = yield result;
            if (updatedEntity) {
                // emit event after assign
                CoreEvent.dispatch(AssignEntityEvent.Assign, {
                    id: assignEntityComponentId
                });
            }
        }));
    }, [
        assignEntityComponentId,
        filter,
        filterTarget,
        multiple,
        queryId,
        assignEntity
    ]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: AssignEntityCreateButtonPlugin }, props),
        React.createElement(SiteButtonBase, { siteButtonProps: (_g = component.props) === null || _g === void 0 ? void 0 : _g.siteButtonProps, buttonProps: Object.assign(Object.assign({}, hoverProps(component)), { disabled: isLoading, className: classes.join(' '), onClick: event => {
                    // NOTE: main difference with SiteButton onClick
                    // is including extra event data to change the behavior
                    // of the SiteCreateEntityDialog component
                    if (editing || !assignEntityComponent) {
                        return;
                    }
                    event.stopPropagation();
                    const data = {
                        assignEntityComponentId: assignEntityComponent.id,
                        disableRouteRedirect: true
                    };
                    emit('onClick', data);
                } }) }),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setState(state => (Object.assign(Object.assign({}, state), { error: undefined }))) }, (error === null || error === void 0 ? void 0 : error.message) || 'An error occurred.')));
};
export const AssignEntityCreateButtonConfigurator = props => {
    const { component } = props;
    // satisfy types, make sure values are set
    if (!component.props) {
        component.props = {};
    }
    if (!component.props.siteButtonProps) {
        component.props.siteButtonProps = {};
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(AssignEntityWarning, { component: component }),
        React.createElement(SiteButtonBaseConfigurator, { component: component, basePath: 'props.siteButtonProps', siteButtonProps: component.props.siteButtonProps })));
};
export const AssignEntityCreateButtonPlugin = {
    name: 'Assign Entity Create Button',
    type: 'assign-entity-create-button',
    component: AssignEntityCreateButton,
    configurator: AssignEntityCreateButtonConfigurator,
    initConfig: component => {
        if (!component.props) {
            component.props = {};
        }
        if (!component.props.siteButtonProps) {
            component.props.siteButtonProps = {};
        }
        const { siteButtonProps } = component.props;
        siteButtonProps.color = 'primary';
        siteButtonProps.variant = 'contained';
        siteButtonProps.text = 'Create';
        return component;
    }
};
//# sourceMappingURL=AssignEntityCreateButtonPlugin.js.map